import type { Shipping } from './shipping';

/**
 * What a failed delivery actually means.
 *
 * The executor maps its error text to one of four codes, and the last is a
 * fallback: anything it does not recognise becomes `DELIVERY_FAILED`. Rendered
 * as itself that reads "Delivery failed: delivery failed", which tells an
 * operator nothing and hides the one cause they can act on.
 *
 * The common case behind that fallback is a run whose manifest is empty. The
 * executor refuses it because a package with no placed file has nothing to
 * copy, and that refusal is a curation problem rather than a transfer problem.
 * The shipping state distinguishes them, so the message can say which.
 */

const FAILURE: Record<string, string> = {
  // Both of these were the generic code until the executor learned to tell a
  // validation refusal from a transfer fault. They are curation problems on
  // the run, and they send someone somewhere completely different.
  PACKAGE_EMPTY:
    'The package holds no placed files, so there was nothing to copy and the delivery was refused before it created anything. This is the package rather than the transfer: take stock of every file on a run that is still structuring.',
  // No remedy in the sentence: whether one is available depends on the run,
  // and this map does not know the run. `deliveryFailure` appends it, and only
  // when it is actually available - telling an operator to "request it again"
  // on a run that cannot accept a request is worse than saying nothing.
  MANIFEST_COUNT_CHANGED:
    'The package changed between the request and the copy, so the delivery stopped rather than hand over a different set of files than the one that was promised. Nothing was copied and nothing was lost.',
  DESTINATION_BUCKET_UNAVAILABLE:
    'The destination bucket could not be created or written to, which is an account or region condition rather than anything wrong with the package. It was retried automatically and did not clear, so it needs someone with access to that account.',
  SOURCE_READ_FAILED:
    'A source object could not be read. It was retried automatically and did not clear; every object already copied is kept, so what remains is the remainder rather than the whole.',
  DELIVERED_COPY_DIGEST_MISMATCH:
    'A copied object did not match its source digest, so the delivery stopped rather than certify bytes it could not vouch for. Nothing was marked delivered.',
};

export function deliveryFailure(code: string, ship?: Shipping, canRequestAgain?: boolean): string {
  /**
   * The remedy is appended, not baked in, and only when it exists.
   *
   * On aiinsurance26 a delivery failed on a count that a later migration made
   * correct, and the card told the operator to "re-check the gate and request
   * it again" - on a run held at GATE_PENDING, which accepts neither. An
   * instruction that cannot be followed is worse than an outcome with no
   * instruction: it reads as the operator's failure to do the obvious thing.
   */
  const remedy = canRequestAgain
    ? ' Re-check the gate and request it again.'
    : ' This run cannot accept another delivery request while it is held, so there is nothing to press: it is recorded here rather than shown as something for you to fix.';
  const known = FAILURE[code];
  if (known) return `Delivery failed. ${known}${remedy}`;
  if (ship?.known && !ship.willShip) {
    // The executor now names an empty package with its own code, so reaching
    // here with an empty run means the failure was something else. Saying so
    // keeps the empty package from absorbing the blame for every fault.
    return (
      'Delivery failed without recording a cause. This run also has no placed files, though an empty package has ' +
      'its own code and this is not it, so the two are separate facts. The delivery record and its logs are what ' +
      'say why.'
    );
  }
  // Never a log-diving instruction. A causeless failure is retried by the
  // page; if it is still on screen the retries are spent, and the next step is
  // a new run rather than a search through logs.
  return 'This did not get through and no cause was recorded. It was retried automatically and did not clear, so this run cannot deliver: start a new one, and every decision made here stays on the record.';
}
